import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import ModalCustom from '../../UI/modal';
import { useAddTripPhoto } from '../../../hooks/use-add-trip-photo';
import aiService from '../../../services/ai.service';
import ThInputText from '../../forms/th-input-text';
import Button from '../../UI/button';
import { IPlacesModel } from '../activity/use-add-activity';
import { ISearchBiasModel } from '../../../Models/search-bias.model';
import {
  placesAutocomplete,
  getPlaceDetails
} from '../../../services/map.service';
import { TripCardDisplayEditor } from '../../trips/trip-card-display-editor';

type Props = {
  show: boolean;
  handleClose: () => void;
  tripId: string;
  bias?: ISearchBiasModel;
  onSuccess?: () => void;
};

const AddTripPhotoModal = ({
  show,
  handleClose,
  tripId,
  bias,
  onSuccess
}: Props) => {
  // Post-upload prompt state
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedWithPlace, setUploadedWithPlace] = useState(false);
  const [showCardEditor, setShowCardEditor] = useState(false);

  // Intercept onClose so we can show the prompt before closing
  const handleCloseForHook = () => {
    // Hook calls this after success; we leave the modal open to show the prompt
    if (!uploadComplete) {
      handleClose();
    }
  };

  const handleSuccessForHook = () => {
    setUploadedWithPlace(!!foundPlace);
    setUploadComplete(true);
    // Notify parent of the upload so it can refresh data
    if (onSuccess) onSuccess();
  };

  const {
    photoData,
    validated,
    isSubmitting,
    handleInputChange,
    handleSubmit
  } = useAddTripPhoto(tripId, handleCloseForHook, handleSuccessForHook);

  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [foundPlace, setFoundPlace] = useState<IPlacesModel | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setFoundPlace(null);
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      setPreview(dataUrl);
      setUploading(true);

      try {
        // Extract EXIF metadata from the image
        const exifData = await extractExifData(file);

        // Process image with AI
        const res = await aiService.processImage(dataUrl);
        const modelText = res?.data;
        let parsed: any = null;

        if (typeof modelText === 'string') {
          try {
            parsed = JSON.parse(modelText);
          } catch (err) {
            // Try to extract JSON substring
            const match = modelText.match(/\{[\s\S]*\}/);
            if (match) {
              try {
                parsed = JSON.parse(match[0]);
              } catch (e) {
                console.warn('Could not parse extracted JSON');
              }
            }
          }
        } else if (typeof modelText === 'object') {
          parsed = modelText;
        }

        // Set the image data URL
        handleInputChange('imageUrl', dataUrl);

        // Use EXIF GPS data if available
        let latitude = exifData?.latitude;
        let longitude = exifData?.longitude;
        let altitude = exifData?.altitude;

        if (latitude && longitude) {
          handleInputChange('latitude', latitude);
          handleInputChange('longitude', longitude);
          if (altitude) {
            handleInputChange('altitude', altitude);
          }

          // Try to find a place near these coordinates (within 1 mile / ~1600 meters)
          try {
            const nearbyPlace = await findNearbyPlace(
              latitude,
              longitude,
              bias
            );
            if (nearbyPlace) {
              setFoundPlace(nearbyPlace);
              handleInputChange('placeId', nearbyPlace.placesId);
              handleInputChange('placeName', nearbyPlace.name);
            }
          } catch (err) {
            console.warn('Error finding nearby place:', err);
          }
        }

        // Use AI suggestions if available
        if (parsed) {
          const { suggestedTitle, description } = parsed;

          if (suggestedTitle) {
            handleInputChange('caption', suggestedTitle);
          } else if (description) {
            handleInputChange('caption', description);
          }
        }
      } catch (err: any) {
        console.error('Error processing image:', err);
        setError(err?.message || 'Error processing image');
      } finally {
        setUploading(false);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validated) {
      handleSubmit(e);
    }
  };

  return (
    <>
      <ModalCustom
        show={show}
        onHide={handleClose}
        title='Add Trip Photo'
      >
        {uploadComplete ? (
          /* ── Post-upload prompt ─────────────────────────────────────────── */
          <div style={{ padding: '1rem 0', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📷</div>
            <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
              Photo added!
            </p>
            {uploadedWithPlace && foundPlace ? (
              <>
                <p
                  style={{
                    color: '#555',
                    fontSize: '0.875rem',
                    marginBottom: '1rem'
                  }}
                >
                  Include this photo in <strong>{foundPlace.name}</strong>'s
                  slideshow?
                </p>
                <div
                  style={{
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'center'
                  }}
                >
                  <Button
                    variant='primary'
                    onClick={() => {
                      // TODO: call activityDisplayService.updateDisplayState to append photo
                      // For now, close the modal
                      handleClose();
                    }}
                  >
                    Yes, add to slideshow
                  </Button>
                  <Button
                    variant='light'
                    onClick={handleClose}
                  >
                    No thanks
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p
                  style={{
                    color: '#555',
                    fontSize: '0.875rem',
                    marginBottom: '1rem'
                  }}
                >
                  Would you like to add this photo to the trip card slideshow?
                </p>
                <div
                  style={{
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'center'
                  }}
                >
                  <Button
                    variant='primary'
                    onClick={() => {
                      setShowCardEditor(true);
                      handleClose();
                    }}
                  >
                    Yes, edit trip card
                  </Button>
                  <Button
                    variant='light'
                    onClick={handleClose}
                  >
                    No thanks
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          /* ── Upload form ────────────────────────────────────────────────── */
          <Form onSubmit={handleFormSubmit}>
            <Form.Group className='mb-3'>
              <Form.Label>Upload a Photo</Form.Label>
              <Form.Control
                type='file'
                accept='image/*'
                onChange={handleFileChange}
              />
              <Form.Text className='text-muted'>
                Photos with GPS data will be automatically located on your trip
                map
              </Form.Text>
            </Form.Group>

            {preview && (
              <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                <img
                  src={preview}
                  alt='preview'
                  style={{
                    maxWidth: '100%',
                    maxHeight: 240,
                    borderRadius: '8px'
                  }}
                />
              </div>
            )}

            {error && <div className='text-danger mb-2'>{error}</div>}

            {foundPlace && (
              <div className='alert alert-info mb-3'>
                <strong>📍 Location Found:</strong> {foundPlace.name}
                <br />
                <small className='text-muted'>
                  This photo will be linked to this location
                </small>
              </div>
            )}

            <ThInputText
              label='Caption (Optional)'
              placeholder='Add a caption for this photo'
              value={photoData.caption ?? ''}
              onChange={(value: string) => handleInputChange('caption', value)}
            />

            {photoData.latitude && photoData.longitude && (
              <div className='text-muted small mb-3'>
                <strong>GPS Coordinates:</strong>{' '}
                {photoData.latitude.toFixed(6)},{' '}
                {photoData.longitude.toFixed(6)}
              </div>
            )}

            <Button
              className='mx-auto'
              style={{ marginTop: '24px' }}
              variant='primary'
              type='submit'
              disabled={!validated || isSubmitting || uploading}
            >
              {isSubmitting || uploading ? 'Adding Photo...' : 'Add Photo'}
            </Button>
          </Form>
        )}
      </ModalCustom>

      {/* Trip card editor — opened from "Yes, edit trip card" prompt */}
      {showCardEditor && (
        <TripCardDisplayEditor
          tripId={tripId}
          tripTitle='Trip Card'
          onClose={() => setShowCardEditor(false)}
          onSaved={() => setShowCardEditor(false)}
        />
      )}
    </>
  );
};

// Helper function to extract EXIF data from image
async function extractExifData(file: File): Promise<{
  latitude?: number;
  longitude?: number;
  altitude?: number;
} | null> {
  // EXIF extraction will happen on the backend
  return null;
}

// Helper function to find nearby place
async function findNearbyPlace(
  latitude: number,
  longitude: number,
  bias?: ISearchBiasModel
): Promise<IPlacesModel | null> {
  try {
    // Search for places near these coordinates (within ~1 mile / 1600 meters)
    // Use the places service to search for nearby places
    const sessionToken = uuidv4();

    // Search with the coordinates as the query
    const query = `${latitude},${longitude}`;

    // Use autocomplete to find nearby places
    const autoRes = await placesAutocomplete(query, sessionToken, bias);

    if (autoRes?.status === 'success' && autoRes.data.suggestions.length > 0) {
      const top = autoRes.data.suggestions[0];
      const placeId =
        top.placePrediction?.placeId || top.placeId || top.place_id || '';
      const details = await getPlaceDetails(placeId, sessionToken);

      if (details?.status === 'success' && details.data) {
        const place = details.data;
        return {
          placesId: placeId,
          name: place.displayName?.text || '',
          latitude: place.location?.latitude || latitude,
          longitude: place.location?.longitude || longitude
        } as IPlacesModel;
      }
    }

    return null;
  } catch (error) {
    console.warn('Error finding nearby place:', error);
    return null;
  }
}

export default AddTripPhotoModal;
